import { z } from "zod";
import { type OAuthClientCreate, type OAuthClientResponse, type OAuthClientUpdate, schemas } from "@/api";

/** Split a free-text list (newlines, spaces or commas) into its non-empty entries. */
export const splitList = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export const DEFAULT_SCOPES = ["openid", "profile", "email"];
export const DEFAULT_GRANTS = ["authorization_code", "refresh_token"];

// Rules come from the generated OAuthClientCreate schema (name length, client type pattern).
const create = schemas.zOAuthClientCreate.shape;

export function oauthClientSchema(redirectsRequired: string) {
  return z.object({
    clientName: create.clientName,
    clientType: create.clientType.unwrap().unwrap(),
    redirects: z.string().refine((text) => splitList(text).length > 0, redirectsRequired),
    scopes: z.string(),
    requirePkce: z.boolean(),
  });
}
export type OAuthClientValues = z.infer<ReturnType<typeof oauthClientSchema>>;

export function formValues(client?: OAuthClientResponse): OAuthClientValues {
  return {
    clientName: client?.clientName ?? "",
    clientType: client?.clientType ?? "public",
    redirects: (client?.redirectUris ?? []).join("\n"),
    scopes: (client?.allowedScopes ?? DEFAULT_SCOPES).join(" "),
    requirePkce: client?.requirePkce ?? true,
  };
}

export function toCreateBody(values: OAuthClientValues): OAuthClientCreate {
  return {
    clientName: values.clientName.trim(),
    clientType: values.clientType,
    redirectUris: splitList(values.redirects),
    allowedScopes: splitList(values.scopes),
    grantTypes: DEFAULT_GRANTS,
    requirePkce: values.requirePkce,
  };
}

export function toUpdateBody(values: OAuthClientValues): OAuthClientUpdate {
  return {
    clientName: values.clientName.trim(),
    redirectUris: splitList(values.redirects),
    allowedScopes: splitList(values.scopes),
    requirePkce: values.requirePkce,
  };
}
